import React from 'react'
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button,
  Container,
  Stack
} from '@mui/material'
import { 
  Business as BusinessIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material'

interface NoDataScreenProps {
  companyName?: string
  onRetry?: () => void
  isLoading?: boolean
}

const NoDataScreen: React.FC<NoDataScreenProps> = ({ 
  companyName = "sua empresa", 
  onRetry,
  isLoading = false 
}) => {
  return (
    <Container maxWidth="lg" sx={{ py: 6, pb: 8, minHeight: '60vh' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: '50vh',
          gap: 4
        }}
      >
        {/* Ícone Principal */}
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: 3
          }}
        >
          <AnalyticsIcon sx={{ fontSize: 50, color: 'white' }} />
        </Box>

        {/* Título Principal */}
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 'bold',
            color: 'text.primary',
            mb: 1
          }}
        >
          Bem-vindo ao Lognex Insights
        </Typography>

        {/* Subtítulo */}
        <Typography 
          variant="h6" 
          color="text.secondary" 
          gutterBottom
          sx={{ mb: 4 }}
        >
          {companyName}
        </Typography>

        {/* Grid de Cards */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
          gap: 3, 
          width: '100%',
          maxWidth: 800,
          mb: 6
        }}>
          {/* Card Principal */}
          <Card 
            sx={{ 
              gridColumn: { xs: '1', md: '1 / -1' },
              boxShadow: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={3} alignItems="center">
                {/* Ícone de Empresa */}
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: 'secondary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 30, color: 'white' }} />
                </Box>

                {/* Mensagem Principal */}
                <Typography 
                  variant="h6" 
                  color="text.primary"
                  sx={{ fontWeight: 'medium' }}
                >
                  Sua empresa ainda não possui dados analisados
                </Typography>

                {/* Descrição */}
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  Para começar a visualizar insights e análises, é necessário que sua empresa 
                  tenha dados de operações logísticas registrados no sistema.
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* Card de Dados Necessários */}
          <Card 
            sx={{ 
              boxShadow: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              backgroundColor: 'info.light',
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    backgroundColor: 'info.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 25, color: 'white' }} />
                </Box>
                <Typography variant="h6" color="text.primary" sx={{ fontWeight: 'medium' }}>
                  Dados Necessários
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  CTEs, NFEs, clientes e operações logísticas
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* Card de Próximos Passos */}
          <Card 
            sx={{ 
              boxShadow: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              backgroundColor: 'success.light',
              background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <AssessmentIcon sx={{ fontSize: 25, color: 'white' }} />
                </Box>
                <Typography variant="h6" color="text.primary" sx={{ fontWeight: 'medium' }}>
                  Próximos Passos
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Entre em contato com o suporte para configurar a integração de dados
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Botões de Ação */}
        <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 4 }}>
          {onRetry && (
            <Button
              variant="contained"
              size="large"
              onClick={onRetry}
              disabled={isLoading}
              sx={{ 
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem'
              }}
            >
              {isLoading ? 'Carregando...' : 'Tentar Novamente'}
            </Button>
          )}
          
          <Button
            variant="outlined"
            size="large"
            href="mailto:diego@lognex.com.br"
            sx={{ 
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            Entrar em Contato
          </Button>
        </Stack>

        {/* Informações Adicionais */}
        <Box sx={{ mt: 4, mb: 8, maxWidth: 500 }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            O Lognex Insights está configurado para analisar dados específicos da sua empresa, 
            garantindo total privacidade e segurança das informações.
          </Typography>
        </Box>
      </Box>
    </Container>
  )
}

export default NoDataScreen
